import type { RingSizeData } from '@jouzen/ecom-components';
import { useInView } from 'framer-motion';
import { type RefObject, useRef } from 'react';

import type { UseRingScroller } from '@/app/[locale]/product/_components/HardwarePDP/_hooks/types';
import useCurrentRoute, {
  type CurrentRouteValues,
} from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useCurrentRoute';
import useRingScroller from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useRingScroller';
import type { Order } from '@/app/[locale]/product/_components/HardwarePDP/_types/order';
import type { ModalType } from '@/app/[locale]/product/_components/HardwarePDP/Modals/useModalState';
import useModalState from '@/app/[locale]/product/_components/HardwarePDP/Modals/useModalState';
import useCurrency from '@/app/hooks/useCurrency';
import { type ListProduct, useProductsData } from '@/queries/Products';

import getProduct from '../_utils/getProduct';
import useBuildOrder from './useBuildOrder';

export interface UseOR4PDPPageReturn extends UseRingScroller {
  activeModal: ModalType;
  carouselRef: RefObject<HTMLDivElement | null>;
  closeModal: () => void;
  currencyCode: string;
  currentRoute: CurrentRouteValues;
  isCarouselInView: boolean;
  monthlyMembership: ListProduct | undefined;
  order: Order;
  or4ExtraCharger: ListProduct | undefined;
  product: ListProduct | undefined;
  productsData: ListProduct[] | undefined;
  productsQueryError: unknown;
  ringSizesData: RingSizeData[] | undefined;
  steloGlucoseBiosensor: ListProduct | undefined;
  twoYearWarranty: ListProduct | undefined;
  threeYearWarranty: ListProduct | undefined;
  yearlyMembership: ListProduct | undefined;
  onModalOpen: (modal: ModalType) => void;
  onSizeChange: (value: string) => void;
  onRadioChange: (value: string) => void;
}

const useOR4PDPPage = ({ locale }: { locale: string }): UseOR4PDPPageReturn => {
  const { activeModal, openModal, closeModal } = useModalState();

  const carouselRef = useRef<HTMLDivElement>(null);
  const { currencyCode } = useCurrency();
  const { error: productsQueryError, data: productsData } = useProductsData({
    enabled: true,
    includeInventory: true,
  });
  const currentRoute = useCurrentRoute({ locale });
  const { focusWithinProps, sectionRefs } = useRingScroller();

  const {
    product,
    subscription: monthlyMembership,
    oneYearMembership: yearlyMembership,
    or4ExtraCharger,
    steloGlucoseBiosensor,
    twoYearWarranty,
    threeYearWarranty,
    ringSizesData,
    ...rest
  } = getProduct({
    currentRoute,
    productsData,
  });

  const { onRadioChange, onSizeChange, order } = useBuildOrder({
    currentRoute,
    productsData,
    ringSizesData,
    ...rest,
  });

  const isCarouselInView = useInView(carouselRef, {
    margin: '-100% 0% 0% 0%',
  });

  const handleModalOpen = (val: ModalType): void => {
    openModal(val);
  };

  return {
    activeModal,
    carouselRef,
    closeModal,
    currencyCode,
    currentRoute,
    focusWithinProps,
    isCarouselInView,
    monthlyMembership,
    yearlyMembership,
    twoYearWarranty,
    threeYearWarranty,
    order,
    or4ExtraCharger,
    product,
    productsData,
    productsQueryError,
    ringSizesData,
    sectionRefs,
    steloGlucoseBiosensor,
    onModalOpen: handleModalOpen,
    onRadioChange,
    onSizeChange,
  };
};

export default useOR4PDPPage;
